/**
 * KB Manager · Sprint P0 RAG · gestión completa de la Knowledge Base de AG-07.
 *
 * Acciones:
 *   - Listar todos los docs en Mongo (copilot_kb_documents)
 *   - Ver contenido completo de un doc
 *   - Upload markdown nuevo (drag-drop file o paste)
 *   - Borrar un doc (también borra sus chunks)
 *   - Sync filesystem → Mongo (botón explícito)
 *   - Cargar baked embeddings (instant)
 *   - Reindex desde Mongo
 *   - Mostrar Health del sistema RAG
 *
 * Backend: /api/founder-os/copilot/{kb,kb/{slug},seed-kb,load-baked,index,health}
 */
import { useEffect, useState, useRef } from "react";
import {
  Database, Upload, Trash, ArrowsClockwise, Download, FileText, X,
  CheckCircle, Warning, Plus, FloppyDisk, FolderOpen, BookOpen,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";

export default function KnowledgeBaseManager() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [kbRes, healthRes] = await Promise.all([
        api.get("/founder-os/copilot/kb"),
        api.get("/founder-os/copilot/health"),
      ]);
      setDocs(kbRes.data?.documents || []);
      setHealth(healthRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error cargando KB");
    } finally {
      setLoading(false);
    }
  };

  const showInfo = (msg) => {
    setInfo(msg);
    setTimeout(() => setInfo(null), 4500);
  };

  const handleSeed = async () => {
    setBusy("seed");
    setError(null);
    try {
      const { data } = await api.post("/founder-os/copilot/seed-kb");
      showInfo(`Sync filesystem → Mongo · ${data.stats.inserted} insertados · ${data.stats.updated} actualizados · ${data.stats.unchanged} sin cambios`);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const handleLoadBaked = async () => {
    if (!window.confirm("Cargar pre-baked chunks · sobrescribirá los actuales si fuerza?\n\nAcción recomendada cuando la KB está vacía tras un deploy.")) return;
    setBusy("baked");
    setError(null);
    try {
      const { data } = await api.post("/founder-os/copilot/load-baked");
      if (data.loaded) {
        showInfo(`Pre-baked cargado · ${data.chunks_inserted} chunks · modelo: ${data.baked_model?.split("/").pop()}`);
      } else {
        setError(`No cargado: ${data.reason}`);
      }
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const handleReindex = async () => {
    if (!window.confirm("Reindexar embeddings desde Mongo?\n\nRecomputa chunks desde copilot_kb_documents. Si el embedder no carga, indexa solo BM25.")) return;
    setBusy("reindex");
    setError(null);
    try {
      const { data } = await api.post("/founder-os/copilot/index");
      showInfo(`Reindex iniciado en background · pollea para ver progreso · ${data.already_running ? "(ya estaba corriendo)" : ""}`);
      // Poll progress
      const poll = setInterval(async () => {
        try {
          const h = await api.get("/founder-os/copilot/health");
          setHealth(h.data);
          if (!h.data.reindex_job?.running) {
            clearInterval(poll);
            await refresh();
          }
        } catch (_) { clearInterval(poll); }
      }, 3000);
      // Safety stop
      setTimeout(() => clearInterval(poll), 5 * 60 * 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Borrar el documento "${slug}"?\n\nTambién se borran sus chunks asociados. Puedes resubirlo después.`)) return;
    setBusy(`del:${slug}`);
    try {
      const { data } = await api.delete(`/founder-os/copilot/kb/${slug}`);
      showInfo(`Borrado · ${data.chunks_removed} chunks removidos`);
      await refresh();
      if (selectedDoc?.slug === slug) setSelectedDoc(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const handleView = async (slug) => {
    try {
      const { data } = await api.get(`/founder-os/copilot/kb/${slug}`);
      setSelectedDoc(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="kb-manager-root">
      {/* Header / Health */}
      <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold">
              <BookOpen size={12} /> KB Manager
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">Knowledge Base · AG-07</h2>
            <p className="text-xs text-[var(--ag-text-dim)] mt-1">Mongo es la única fuente de verdad · upload markdown · pre-baked load · reindex sobre demanda.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setUploadOpen(true)}
              disabled={!!busy}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold hover:opacity-90 disabled:opacity-50"
              data-testid="kb-upload-open"
            >
              <Upload size={13} /> Subir markdown
            </button>
            <button
              onClick={handleLoadBaked}
              disabled={!!busy}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/20 disabled:opacity-50"
              data-testid="kb-load-baked"
              title="Carga instant del JSON pre-baked"
            >
              <Download size={13} className={busy === "baked" ? "animate-pulse" : ""} /> Cargar baked
            </button>
            <button
              onClick={handleSeed}
              disabled={!!busy}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] disabled:opacity-50"
              data-testid="kb-seed-filesystem"
              title="Sync /app/memory/*.md → Mongo"
            >
              <FolderOpen size={13} /> Sync filesystem
            </button>
            <button
              onClick={handleReindex}
              disabled={!!busy}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] disabled:opacity-50"
              data-testid="kb-reindex"
            >
              <ArrowsClockwise size={13} className={busy === "reindex" || health?.reindex_job?.running ? "animate-spin" : ""} /> Reindexar
            </button>
          </div>
        </div>

        {/* Health summary cards */}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="kb-health-summary">
            <HealthBadge
              icon={<Database size={14} />}
              label="KB Docs"
              value={health.kb_documents?.count || 0}
              tone="info"
              testid="kb-health-docs"
            />
            <HealthBadge
              icon={<FileText size={14} />}
              label="Chunks"
              value={`${health.chunks?.with_embeddings || 0}/${health.chunks?.total || 0}`}
              tone={health.chunks?.with_embeddings === health.chunks?.total ? "ok" : "warn"}
              testid="kb-health-chunks"
            />
            <HealthBadge
              icon={health.embedder?.loaded ? <CheckCircle size={14} /> : <Warning size={14} />}
              label="Embedder"
              value={health.embedder?.loaded ? "Cargado" : (health.embedder?.load_failed ? "Falló" : "No cargado")}
              tone={health.embedder?.loaded ? "ok" : (health.embedder?.load_failed ? "fail" : "neutral")}
              testid="kb-health-embedder"
            />
            <HealthBadge
              icon={health.bm25?.ready ? <CheckCircle size={14} /> : <Warning size={14} />}
              label="BM25"
              value={health.bm25?.ready ? `Ready · ${health.bm25?.indexed_chunks}` : "Pendiente"}
              tone={health.bm25?.ready ? "ok" : "neutral"}
              testid="kb-health-bm25"
            />
          </div>
        )}

        {/* Overall message */}
        {health?.message && (
          <div className={`mt-3 px-3 py-2 rounded-sm text-xs ${
            health.overall === "healthy" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" :
            health.overall === "degraded" ? "bg-amber-500/10 border border-amber-500/30 text-amber-300" :
            health.overall === "warning" ? "bg-amber-500/10 border border-amber-500/30 text-amber-300" :
            "bg-red-500/10 border border-red-500/30 text-red-300"
          }`} data-testid="kb-overall-message">
            <strong className="uppercase tracking-wide text-[10px] mr-2">{health.overall}</strong>
            {health.message}
          </div>
        )}

        {/* Reindex job progress */}
        {health?.reindex_job?.running && (
          <div className="mt-3 p-3 rounded-sm bg-[var(--ag-gold)]/5 border border-[var(--ag-gold)]/30" data-testid="kb-reindex-progress">
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-[var(--ag-gold)]">
                <ArrowsClockwise size={12} className="animate-spin" />
                <strong>Reindexando · modo {health.reindex_job.mode || "init"}</strong>
              </div>
              <div className="text-[10px] text-[var(--ag-text-dim)] tabular-nums">
                {health.reindex_job.files_done}/{health.reindex_job.files_total || "?"} files · {health.reindex_job.chunks_total} chunks
              </div>
            </div>
            <div className="mt-2 text-[10px] text-[var(--ag-text-muted)]">
              Actual: <code>{health.reindex_job.current_file || "preparando…"}</code>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-[var(--ag-bg)] overflow-hidden">
              <div className="h-full bg-[var(--ag-gold)] transition-all" style={{
                width: health.reindex_job.files_total > 0
                  ? `${Math.round((health.reindex_job.files_done / health.reindex_job.files_total) * 100)}%`
                  : "10%",
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Inline messages */}
      {error && (
        <div className="rounded-sm border border-red-500/30 bg-red-500/10 text-red-300 text-xs px-3 py-2" data-testid="kb-error">
          <strong>Error:</strong> {error}
        </div>
      )}
      {info && (
        <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs px-3 py-2" data-testid="kb-info">
          {info}
        </div>
      )}

      {/* Docs table */}
      <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] overflow-hidden" data-testid="kb-docs-table">
        <div className="px-4 py-3 border-b border-[var(--ag-border)] flex items-center justify-between">
          <div className="text-sm font-semibold text-[var(--ag-text)]">Documentos · {docs.length}</div>
          <button onClick={refresh} className="text-[11px] text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] inline-flex items-center gap-1">
            <ArrowsClockwise size={11} /> Recargar
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-[var(--ag-text-dim)]">Cargando…</div>
        ) : docs.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--ag-text-dim)]" data-testid="kb-empty">
            No hay documentos en la KB. Carga el pre-baked o sube un markdown.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--ag-midnight)]/30">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)]">
                  <th className="px-4 py-2.5">Archivo</th>
                  <th className="px-3 py-2.5">Slug</th>
                  <th className="px-3 py-2.5 text-right">Chars</th>
                  <th className="px-3 py-2.5 text-right">Chunks</th>
                  <th className="px-3 py-2.5">Origen</th>
                  <th className="px-3 py-2.5">Actualizado</th>
                  <th className="px-3 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.slug} className="border-t border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/15" data-testid={`kb-row-${d.slug}`}>
                    <td className="px-4 py-2.5 text-[var(--ag-text)] font-mono text-[11px]">{d.filename}</td>
                    <td className="px-3 py-2.5 text-[var(--ag-text-dim)] font-mono text-[10px]">{d.slug}</td>
                    <td className="px-3 py-2.5 text-right text-[var(--ag-text-dim)] tabular-nums">{d.char_count?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right text-[var(--ag-text-dim)] tabular-nums">{d.chunk_count}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded-sm border ${
                        d.source === "upload"
                          ? "border-[var(--ag-gold)]/40 text-[var(--ag-gold)] bg-[var(--ag-gold)]/10"
                          : "border-[var(--ag-border)] text-[var(--ag-text-muted)]"
                      }`}>
                        {d.source}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[var(--ag-text-muted)] tabular-nums text-[10px]">
                      {d.updated_at ? new Date(d.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => handleView(d.slug)}
                        className="text-[10px] text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] mr-2"
                        data-testid={`kb-view-${d.slug}`}
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => handleDelete(d.slug)}
                        disabled={busy === `del:${d.slug}`}
                        className="text-[10px] text-red-400 hover:text-red-300 disabled:opacity-50"
                        data-testid={`kb-delete-${d.slug}`}
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => { setUploadOpen(false); await refresh(); showInfo("Documento subido · ahora reindexa para generar chunks/embeddings"); }}
        />
      )}

      {/* View Doc Modal */}
      {selectedDoc && (
        <ViewModal doc={selectedDoc} onClose={() => setSelectedDoc(null)} />
      )}
    </div>
  );
}

function HealthBadge({ icon, label, value, tone, testid }) {
  const colors = {
    ok: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    fail: "border-red-500/30 bg-red-500/10 text-red-300",
    info: "border-[var(--ag-gold)]/30 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]",
    neutral: "border-[var(--ag-border)] bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)]",
  };
  return (
    <div className={`rounded-sm border px-3 py-2 ${colors[tone] || colors.neutral}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {icon} {label}
      </div>
      <div className="text-sm font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function UploadModal({ onClose, onUploaded }) {
  const [filename, setFilename] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setContent(e.target.result || "");
    reader.readAsText(file);
  };

  const submit = async () => {
    if (!filename.trim() || !content.trim()) {
      setErr("Filename y content son requeridos");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await api.post("/founder-os/copilot/kb", {
        filename: filename.trim(),
        content,
      });
      onUploaded();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Error subiendo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="kb-upload-modal">
      <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[var(--ag-border)] flex items-center justify-between">
          <h3 className="text-sm font-bold text-[var(--ag-text)]">Subir markdown a la KB</h3>
          <button onClick={onClose} className="text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] mb-1">Nombre del archivo (debe terminar en .md)</label>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="MI_NUEVO_DOC.md"
              className="w-full px-3 py-2 rounded-sm bg-[var(--ag-bg)] border border-[var(--ag-border)] focus:border-[var(--ag-gold)] outline-none text-sm text-[var(--ag-text)] font-mono"
              data-testid="kb-upload-filename"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] mb-1">Contenido (markdown)</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="# Mi nuevo documento&#10;&#10;Contenido aquí…"
              rows={14}
              className="w-full px-3 py-2 rounded-sm bg-[var(--ag-bg)] border border-[var(--ag-border)] focus:border-[var(--ag-gold)] outline-none text-xs text-[var(--ag-text)] font-mono resize-y"
              data-testid="kb-upload-content"
            />
            <div className="mt-1 text-[10px] text-[var(--ag-text-muted)]">{content.length.toLocaleString()} caracteres</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <input
              ref={fileRef}
              type="file"
              accept=".md,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="kb-upload-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              type="button"
              className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)]"
            >
              <Upload size={11} /> Cargar desde archivo
            </button>
            <span className="text-[10px] text-[var(--ag-text-muted)]">o pega el contenido arriba</span>
          </div>
          {err && (
            <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-2 py-1.5">{err}</div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[var(--ag-border)] flex items-center justify-between gap-2">
          <div className="text-[10px] text-[var(--ag-text-muted)]">Tras subir, ejecuta «Reindexar» para generar chunks/embeddings.</div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-sm border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={submitting || !filename.trim() || !content.trim()}
              className="text-xs px-3 py-1.5 rounded-sm bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="kb-upload-submit"
            >
              <FloppyDisk size={11} weight="fill" /> {submitting ? "Subiendo…" : "Subir"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ doc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="kb-view-modal">
      <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[var(--ag-border)] flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[var(--ag-text)] font-mono">{doc.filename}</h3>
            <div className="text-[10px] text-[var(--ag-text-muted)] mt-0.5">{doc.char_count?.toLocaleString()} chars · slug {doc.slug} · origen {doc.source}</div>
          </div>
          <button onClick={onClose} className="text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          <pre className="text-[11px] leading-relaxed text-[var(--ag-text)] whitespace-pre-wrap break-words font-mono">{doc.content}</pre>
        </div>
      </div>
    </div>
  );
}
